// File: frontend/src/api/concursosApi.ts

import { getAuthToken } from "../security/AuthLogin";

const BASE_URL = `${window.location.protocol}//${window.location.hostname}`;
const API_URL = `${BASE_URL}/api`;

/**
 * Lista de concursos con filtros y paginación.
 */
export const fetchConcursos = async (params: {
  [key: string]: string | number | Array<string | number>;
}) => {
  const token = getAuthToken();
  if (!token) {
    window.location.href = "/login";
    throw new Error("Token no encontrado. El usuario no está autenticado.");
  }

  const queryString = new URLSearchParams();
  for (const key in params) {
    const value = params[key];
    if (Array.isArray(value)) {
      value.forEach((val) => queryString.append(key, val.toString()));
    } else {
      queryString.append(key, value.toString());
    }
  }

  const response = await fetch(
    `${API_URL}/concursos?${queryString.toString()}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    if (response.status === 403 || response.status === 401) {
      localStorage.removeItem("authToken");
      window.location.href = "/login";
      throw new Error("Token expirado o inválido");
    }
    const errorText = await response.text();
    throw new Error(`Error al obtener los concursos: ${errorText}`);
  }

  return response.json(); // { data, total, page, limit }
};

/**
 * Obtiene el detalle de un concurso por su id_concurso.
 * -- AQUÍ hacemos la distinción de 404:
 */
export const fetchConcursoById = async (id: string) => {
  const token = getAuthToken();
  if (!token) {
    window.location.href = "/login";
    throw new Error("Token no encontrado. El usuario no está autenticado.");
  }

  const response = await fetch(`${API_URL}/concursos/${id}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      // Armamos un Error con .status=404
      const notFoundError: any = new Error("Concurso no encontrado");
      notFoundError.status = 404;
      throw notFoundError;
    } else {
      // Otros errores
      const errorText = await response.text();
      throw new Error(errorText || "Error al obtener los datos del concurso");
    }
  }

  return response.json();
};

/**
 * Actualiza un concurso (sólo roles admin/tecnico).
 */
export const updateConcurso = async (id: string, updates: any) => {
  const token = getAuthToken();
  if (!token) {
    window.location.href = "/login";
    throw new Error("Token no encontrado. El usuario no está autenticado.");
  }

  const response = await fetch(`${API_URL}/concursos/${id}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    throw new Error("Error al actualizar el concurso");
  }

  return response.json();
};

/**
 * Elimina un concurso (sólo admin).
 */
export const deleteConcurso = async (id: string) => {
  const token = getAuthToken();
  if (!token) {
    window.location.href = "/login";
    throw new Error("Token no encontrado. El usuario no está autenticado.");
  }

  const response = await fetch(`${API_URL}/concursos/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Error al eliminar el concurso");
  }

  return response.json();
};

/**
 * Crea un concurso (sólo admin/tecnico).
 */
export const createConcurso = async (newConcurso: any) => {
  const token = getAuthToken();
  if (!token) {
    window.location.href = "/login";
    throw new Error("Token no encontrado. El usuario no está autenticado.");
  }

  const response = await fetch(`${API_URL}/concursos/crear`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(newConcurso),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Error al crear el concurso: ${errorText}`);
  }

  return response.json();
};

/**
 * Añade o quita un concurso de la lista de favoritos (cliente).
 */
export const toggleFavorito = async (
  idConcurso: string,
  action: "add" | "remove"
) => {
  const token = getAuthToken();
  if (!token) {
    window.location.href = "/login";
    throw new Error("Token no encontrado. El usuario no está autenticado.");
  }

  const response = await fetch(`${API_URL}/concursos/${idConcurso}/favorito`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ action }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Error al cambiar favorito: ${errorText}`);
  }

  return response.json();
};

/**
 * Añade o quita un concurso de la lista de descartados (cliente).
 */
export const toggleDescartado = async (
  idConcurso: string,
  action: "add" | "remove"
) => {
  const token = getAuthToken();
  if (!token) {
    window.location.href = "/login";
    throw new Error("Token no encontrado. El usuario no está autenticado.");
  }

  const response = await fetch(`${API_URL}/concursos/${idConcurso}/descartar`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ action }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Error al descartar/restaurar concurso: ${errorText}`);
  }

  return response.json();
};
